import React, { useState, useEffect } from 'react';
import { getCredentials, setCredentials, isConfigured } from '../services/datatraceApi';
import { PRODUCT_CONFIGS, STATES, COUNTY_OPTIONS, BASE_URL_OPTIONS, getConfigKey } from '../services/datatraceProducts';
import { findDuplicateSearch, createTransactionRecord, getTransactionReport } from '../services/datatraceTransactions';
import useTransactionHistory from '../hooks/useTransactionHistory';
import TransactionHistory from './datatrace/TransactionHistory';
import SavedReports from './datatrace/SavedReports';
import './DataTraceAPITester.css';

function DataTraceAPITester({ onBack }) {
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [state, setState] = useState('MI');
  const [county, setCounty] = useState('Kent');
  const [countyFips, setCountyFips] = useState('');
  const [apn, setApn] = useState('');

  // Parties for TIQSA (required field)
  const [parties, setParties] = useState([
    {
      party_entity_type: 'individual',
      party_role: 'owner',
      first_name: '',
      last_name: '',
      middle_name: '',
      suffix: '',
      full_name: ''
    }
  ]);

  // API Configuration - Multiple products can be selected
  const [selectedProducts, setSelectedProducts] = useState([
    {
      baseUrl: 'https://uat.gateway.datatracetitle.com/ddg',
      productCode: 'property_data',
      productType: 'property_detail',
      endpoint: 'property_data_sync'
    }
  ]);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [showRawRequest, setShowRawRequest] = useState(false);
  const [showRawResponse, setShowRawResponse] = useState(true);

  // Credentials management
  const [credentialsConfigured, setCredentialsConfigured] = useState(false);
  const [showCredentialsSetup, setShowCredentialsSetup] = useState(false);
  const [secretLast3, setSecretLast3] = useState('');

  // Transaction history management using custom hook
  const [showHistory, setShowHistory] = useState(false);
  const [saveSuccessModal, setSaveSuccessModal] = useState(null);
  const [savingReport, setSavingReport] = useState({});
  const [activeView, setActiveView] = useState('tester'); // tester, saved-reports
  const {
    transactionHistory,
    loadingHistory,
    pollingTransactions,
    saveTransaction,
    // updateTransactionStatus is used internally by checkStatus

    checkStatus
  } = useTransactionHistory();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    setCredentialsConfigured(isConfigured());
  }, []);

  const handleProductToggle = (config) => {
    // Don't allow toggling Custom product
    if (config.productCode === 'custom') {
      return;
    }

    const configKey = getConfigKey(config);
    const isSelected = selectedProducts.some(p => getConfigKey(p) === configKey);

    if (isSelected) {
      // Remove if already selected
      setSelectedProducts(selectedProducts.filter(p => getConfigKey(p) !== configKey));
    } else {
      // Add if not selected
      setSelectedProducts([...selectedProducts, {
        baseUrl: config.baseUrl,
        productCode: config.productCode,
        productType: config.productType,
        endpoint: config.endpoint
      }]);
    }
  };

  const isProductSelected = (config) => {
    const configKey = getConfigKey(config);
    return selectedProducts.some(p => getConfigKey(p) === configKey);
  };

  const handleCountyChange = (e) => {
    const selectedCounty = e.target.value;
    setCounty(selectedCounty);

    // Auto-fill county FIPS code for Kent and Ottawa
    // NOTE: Leave empty - server will fill in the correct full FIPS code
    const fipsMap = {
      'Kent': '',
      'Ottawa': ''
    };
    setCountyFips(fipsMap[selectedCounty] || '');
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!address.trim()) {
      alert('Please enter an address');
      return;
    }

    if (selectedProducts.length === 0) {
      alert('Please select at least one product');
      return;
    }

    // Check for duplicate searches using service
    const searchParams = {
      address: address.trim(),
      city: city.trim(),
      zip: zip.trim(),
      state,
      county,
      countyFips: countyFips.trim(),
      apn: apn.trim()
    };

    setLoading(true);

    const credentials = getCredentials();
    const newResults = [];

    // Submit API calls for all selected products
    for (const product of selectedProducts) {
      const { baseUrl, productCode, productType, endpoint } = product;

      // Check for duplicates for this specific product
      const duplicate = findDuplicateSearch(transactionHistory, searchParams, productCode, productType);

      if (duplicate) {
        const confirmed = window.confirm(
          `⚠️ DUPLICATE SEARCH WARNING\n\n` +
          `You already submitted a search for this address with product ${productCode} / ${productType}:\n\n` +
          `Address: ${duplicate.address}\n` +
          `Submitted: ${new Date(duplicate.timestamp).toLocaleString()}\n` +
          `Transaction ID: ${duplicate.transactionId}\n` +
          `Status: ${duplicate.status || 'Pending'}\n\n` +
          `Submitting again will charge you for another report!\n\n` +
          `Do you want to proceed with ${productCode} anyway?`
        );

        if (!confirmed) {
          continue; // Skip this product
        }
      }

      try {
        // Build request body
        const requestBody = {
          credentials: {
            clientId: credentials.clientId,
            clientSecret: credentials.clientSecret,
            scope: credentials.scope
          },
          searchParams,
          apiConfig: {
            base_url: baseUrl,
            product_code: productCode,
            product_type: productType,
            endpoint: endpoint
          }
        };

        // Add parties for TIQSA requests
        if (productCode === 'tiqsa') {
          requestBody.parties = parties.filter(p => p.full_name || p.first_name || p.last_name);
        }

        const response = await fetch('/api/datatrace/search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        const responseText = await response.text();
        let data;

        try {
          data = JSON.parse(responseText);
        } catch (e) {
          data = {
            error: 'Failed to parse response',
            rawResponse: responseText
          };
        }

        // Add to results history
        const result = {
          timestamp: new Date().toISOString(),
          success: response.ok,
          statusCode: response.status,
          baseUrl,
          productCode,
          productType,
          endpoint,
          searchParams,
          requestBody: data.request || {
            product_code: productCode,
            product_type: productType,
            properties: [searchParams]
          },
          response: data,
          rawResponse: responseText
        };

        newResults.push(result);

        // Save to persistent storage if transaction ID exists
        if (data.data?.transaction_id) {
          const productConfig = { productCode, productType, endpoint };
          const transaction = createTransactionRecord(searchParams, productConfig, data, result.requestBody);
          await saveTransaction(transaction);
        }

      } catch (error) {
        const errorResult = {
          timestamp: new Date().toISOString(),
          success: false,
          error: error.message,
          baseUrl,
          productCode,
          productType,
          endpoint,
          searchParams,
          requestBody: {
            product_code: productCode,
            product_type: productType,
            properties: [searchParams]
          }
        };

        newResults.push(errorResult);
      }
    }

    // Add all new results
    setResults([...newResults, ...results]);
    setLoading(false);
  };

  const clearResults = () => {
    if (window.confirm('Clear all test results?')) {
      setResults([]);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Helper to format JSON response properly
  const formatJSON = (rawResponse, parsedResponse) => {
    if (rawResponse) {
      try {
        // Parse the raw response and re-stringify with formatting
        const parsed = JSON.parse(rawResponse);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        // If parsing fails, return raw response
        return rawResponse;
      }
    }
    // Otherwise use the already-parsed response
    return JSON.stringify(parsedResponse, null, 2);
  };

  // Parties management helpers
  const updateParty = (index, field, value) => {
    setParties(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Auto-generate full_name for individuals
      if (updated[index].party_entity_type === 'individual') {
        const parts = [
          updated[index].first_name,
          updated[index].middle_name,
          updated[index].last_name,
          updated[index].suffix
        ].filter(Boolean);
        updated[index].full_name = parts.join(' ');
      }

      return updated;
    });
  };

  const addParty = () => {
    setParties(prev => [...prev, {
      party_entity_type: 'individual',
      party_role: 'owner',
      first_name: '',
      last_name: '',
      middle_name: '',
      suffix: '',
      full_name: ''
    }]);
  };

  const removeParty = (index) => {
    if (parties.length > 1) {
      setParties(prev => prev.filter((_, i) => i !== index));
    }
  };

  const checkTransactionStatus = async (transactionId, resultIndex = null) => {
    try {
      const statusResult = await checkStatus(transactionId);

      // Update the result with status information (if checking from results)
      if (resultIndex !== null) {
        setResults(prev => {
          const newResults = [...prev];
          newResults[resultIndex] = {
            ...newResults[resultIndex],
            statusCheck: statusResult
          };
          return newResults;
        });
      }
    } catch (error) {
      alert(`Failed to check transaction status: ${error.message}`);
    }
  };

  const handleGetReport = async (transactionId, productCode) => {
    try {
      const reportResult = await getTransactionReport(transactionId, productCode);

      // Create a downloadable JSON file
      const dataStr = JSON.stringify(reportResult.report, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${productCode}-report-${transactionId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert(`Report downloaded successfully for transaction ${transactionId}`);
    } catch (error) {
      alert(`Failed to retrieve report: ${error.message}`);
    }
  };

  const handleSaveReportAndDocuments = async (transactionId, productCode, searchParams) => {
    const credentials = getCredentials();

    if (!credentials.clientId || !credentials.clientSecret) {
      alert('DataTrace credentials not configured');
      return;
    }

    setSavingReport(prev => ({ ...prev, [transactionId]: true }));

    try {
      const response = await fetch('/api/datatrace/save-report-and-documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credentials: {
            clientId: credentials.clientId,
            clientSecret: credentials.clientSecret,
            scope: credentials.scope
          },
          transactionId,
          productCode,
          searchParams
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save report and documents');
      }

      // Show success modal with file list
      setSaveSuccessModal({
        directory: result.directory,
        files: result.files,
        transactionId,
        productCode
      });
    } catch (error) {
      alert(`Failed to save report and documents: ${error.message}`);
    } finally {
      setSavingReport(prev => ({ ...prev, [transactionId]: false }));
    }
  };

  const handleSetupCredentials = () => {
    if (secretLast3.length !== 3) {
      alert('Please enter the last 3 characters of the secret key');
      return;
    }

    const creds = getCredentials();
    const fullSecret = creds.partialSecret + secretLast3;
    setCredentials(creds.clientId, fullSecret, creds.scope);
    setCredentialsConfigured(true);
    setShowCredentialsSetup(false);
    setSecretLast3('');
  };

  return (
    <div className="datatrace-api-tester">
      <div className="tester-header">
        <h2>DataTrace API Tester</h2>
        <p className="tester-subtitle">
          Test different DataTrace API products and endpoints
        </p>
      </div>

      {/* Credentials Setup Warning */}
      {!credentialsConfigured && (
        <div className="credentials-warning">
          <div className="warning-icon">⚠️</div>
          <div className="warning-content">
            <h3>DataTrace Credentials Required</h3>
            <p>You need to configure DataTrace API credentials before testing.</p>
            <button
              className="setup-button"
              onClick={() => setShowCredentialsSetup(true)}
            >
              Configure Credentials
            </button>
          </div>
        </div>
      )}

      {/* Credentials Setup Modal */}
      {showCredentialsSetup && (
        <div className="credentials-modal">
          <div className="credentials-modal-content">
            <h3>Configure DataTrace Credentials</h3>
            <p>Enter the last 3 characters of your DataTrace secret key:</p>

            <div className="form-group">
              <label>API Access Code (Last 3 chars):</label>
              <input
                type="text"
                value={secretLast3}
                onChange={(e) => setSecretLast3(e.target.value)}
                maxLength="3"
                placeholder="***"
                className="form-input"
                autoComplete="off"
                data-form-type="other"
                style={{ textAlign: 'center', fontSize: '18px', width: '100px', letterSpacing: '0.3em' }}
              />
              <p className="input-hint">
                Pre-configured partial key: bHR8Q~yJ6SQsxQd2W4AsKQbCuqvv.d27be9Y5***
              </p>
            </div>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  setShowCredentialsSetup(false);
                  setSecretLast3('');
                }}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={handleSetupCredentials}
                disabled={secretLast3.length !== 3}
              >
                Save Credentials
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="datatrace-nav">
        <button
          className={`datatrace-nav-btn ${activeView === 'tester' ? 'active' : ''}`}
          onClick={() => setActiveView('tester')}
        >
          🏠 Select Property
        </button>
        <button
          className={`datatrace-nav-btn ${activeView === 'history' ? 'active' : ''}`}
          onClick={() => setActiveView('history')}
        >
          📋 Transaction History
        </button>
        <button
          className={`datatrace-nav-btn ${activeView === 'saved-reports' ? 'active' : ''}`}
          onClick={() => setActiveView('saved-reports')}
        >
          📁 Saved Reports
        </button>
      </div>

      {activeView === 'saved-reports' ? (
        <SavedReports />
      ) : activeView === 'history' ? (
        /* Transaction History Tab */
        <TransactionHistory
          transactions={transactionHistory}
          loading={loadingHistory}
          pollingTransactions={pollingTransactions}
          onCheckStatus={checkTransactionStatus}
          onGetReport={handleGetReport}
          onSaveReportAndDocuments={handleSaveReportAndDocuments}
          savingReport={savingReport}
          showHistory={true}
          onToggleHistory={() => {}}
        />
      ) : (
        <div className="tester-container">

        {/* Search Form - NOW FIRST */}
        <div className="search-section">
          <h3>Property Search Parameters</h3>
          <form onSubmit={handleSearch} className="search-form">
            <div className="form-group">
              <label htmlFor="address">Street Address <span className="required">*</span></label>
              <input
                type="text"
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="e.g., 123 Main St"
                className="form-input"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g., Grand Rapids"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="zip">ZIP Code</label>
                <input
                  type="text"
                  id="zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="e.g., 49503"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="state">State</label>
                <select
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="form-select"
                >
                  {STATES.map(s => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="county">County</label>
                <input
                  type="text"
                  id="county"
                  value={county}
                  onChange={(e) => setCounty(e.target.value)}
                  placeholder="e.g., Kent"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="countyFips">County FIPS Code</label>
              <input
                type="text"
                id="countyFips"
                value={countyFips}
                onChange={(e) => setCountyFips(e.target.value)}
                placeholder="e.g., 081 for Kent, 139 for Ottawa"
                className="form-input"
                maxLength="3"
              />
              <p className="input-hint" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Leave blank - server will auto-fill the correct FIPS code
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="apn">APN (Optional)</label>
              <input
                type="text"
                id="apn"
                value={apn}
                onChange={(e) => setApn(e.target.value)}
                placeholder="e.g., 41-14-01-100-001"
                className="form-input"
              />
            </div>

            {/* Parties Section - Required for TIQSA */}
            {selectedProducts.some(p => p.productCode === 'tiqsa') && (
              <div className="parties-section" style={{ marginTop: '20px', padding: '15px', border: '2px solid #ffc107', borderRadius: '8px', backgroundColor: '#fffbf0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0, color: '#ffc107' }}>Parties (Required for TIQSA) <span className="required">*</span></h4>
                  <button type="button" onClick={addParty} className="test-button" style={{ padding: '6px 12px', fontSize: '14px' }}>
                    + Add Party
                  </button>
                </div>

                {parties.map((party, index) => (
                  <div key={index} style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h5 style={{ margin: 0 }}>Party {index + 1}</h5>
                      {parties.length > 1 && (
                        <button type="button" onClick={() => removeParty(index)} style={{ color: '#dc3545', border: 'none', background: 'none', cursor: 'pointer', fontSize: '16px' }}>
                          🗑️ Remove
                        </button>
                      )}
                    </div>

                    <div className="form-row" style={{ gap: '10px', marginBottom: '10px' }}>
                      <div className="form-group" style={{ flex: 1 }}>
                        <label>Entity Type</label>
                        <select
                          value={party.party_entity_type}
                          onChange={(e) => updateParty(index, 'party_entity_type', e.target.value)}
                          className="form-select"
                        >
                          <option value="individual">Individual</option>
                          <option value="organization">Organization</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ flex: 1 }}>
                        <label>Party Role</label>
                        <select
                          value={party.party_role}
                          onChange={(e) => updateParty(index, 'party_role', e.target.value)}
                          className="form-select"
                        >
                          <option value="owner">Owner</option>
                          <option value="borrower">Borrower</option>
                          <option value="seller">Seller</option>
                          <option value="buyer">Buyer</option>
                        </select>
                      </div>
                    </div>

                    {party.party_entity_type === 'individual' ? (
                      <>
                        <div className="form-row" style={{ gap: '10px', marginBottom: '10px' }}>
                          <div className="form-group" style={{ flex: 2 }}>
                            <label>First Name <span className="required">*</span></label>
                            <input
                              type="text"
                              value={party.first_name}
                              onChange={(e) => updateParty(index, 'first_name', e.target.value)}
                              className="form-input"
                              placeholder="First name"
                            />
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label>Middle</label>
                            <input
                              type="text"
                              value={party.middle_name}
                              onChange={(e) => updateParty(index, 'middle_name', e.target.value)}
                              className="form-input"
                              placeholder="M"
                            />
                          </div>
                          <div className="form-group" style={{ flex: 2 }}>
                            <label>Last Name <span className="required">*</span></label>
                            <input
                              type="text"
                              value={party.last_name}
                              onChange={(e) => updateParty(index, 'last_name', e.target.value)}
                              className="form-input"
                              placeholder="Last name"
                            />
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label>Suffix</label>
                            <input
                              type="text"
                              value={party.suffix}
                              onChange={(e) => updateParty(index, 'suffix', e.target.value)}
                              className="form-input"
                              placeholder="Jr/Sr"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Full Name (Auto-generated)</label>
                          <input
                            type="text"
                            value={party.full_name}
                            readOnly
                            className="form-input"
                            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                            placeholder="Will be auto-generated from name fields"
                          />
                        </div>
                      </>
                    ) : (
                      <div className="form-group">
                        <label>Organization Name <span className="required">*</span></label>
                        <input
                          type="text"
                          value={party.full_name}
                          onChange={(e) => updateParty(index, 'full_name', e.target.value)}
                          className="form-input"
                          placeholder="Organization name"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

          </form>
        </div>

        {/* Product Configuration Section - NOW SECOND */}
        <div className="config-section">
          <h3>Products</h3>
          <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#666' }}>
            Select one or more products to run for this property search:
          </p>

          <div className="product-cards">
            {PRODUCT_CONFIGS.filter(c => c.productCode !== 'custom').map(config => {
              const isSelected = isProductSelected(config);
              return (
                <div
                  key={getConfigKey(config)}
                  className={`product-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleProductToggle(config)}
                >
                  <div className="product-card-header">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="product-checkbox"
                    />
                    <h4>{config.name}</h4>
                  </div>
                  <p className="product-card-description">{config.description}</p>
                </div>
              );
            })}
          </div>

          {selectedProducts.length > 0 && (
            <div className="selected-products-summary">
              <strong>Selected ({selectedProducts.length}):</strong> {selectedProducts.map(p => PRODUCT_CONFIGS.find(c => getConfigKey(c) === getConfigKey(p))?.name || p.productCode).join(', ')}
            </div>
          )}
        </div>

        {/* Submit Button Section */}
        <div className="form-actions">
          <button
            onClick={handleSearch}
            className="test-button"
            disabled={loading || !address.trim()}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </button>

          {results.length > 0 && (
            <button
              type="button"
              className="clear-button"
              onClick={clearResults}
            >
              Clear Results ({results.length})
            </button>
          )}
        </div>

        {/* Results Section */}
        {results.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Test Results ({results.length})</h3>
              <div className="view-toggles">
                <label>
                  <input
                    type="checkbox"
                    checked={showRawRequest}
                    onChange={(e) => setShowRawRequest(e.target.checked)}
                  />
                  Show Request
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={showRawResponse}
                    onChange={(e) => setShowRawResponse(e.target.checked)}
                  />
                  Show Response
                </label>
              </div>
            </div>

            {results.map((result, index) => (
              <div
                key={index}
                className={`result-card ${result.success ? 'success' : 'error'}`}
              >
                <div className="result-header">
                  <div className="result-info">
                    <span className={`status-badge ${result.success ? 'success' : 'error'}`}>
                      {result.success ? '✓ Success' : '✗ Error'}
                    </span>
                    <span className="status-code">HTTP {result.statusCode}</span>
                    <span className="timestamp">
                      {new Date(result.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="result-config">
                    <span className="config-item">
                      <strong>Full URL:</strong> {result.baseUrl}/{result.endpoint}
                    </span>
                    <span className="config-item">
                      <strong>Request Body:</strong> product_code: "{result.productCode}", product_type: "{result.productType}"
                    </span>
                  </div>
                </div>

                <div className="result-address">
                  <strong>Address:</strong> {result.searchParams.address}, {result.searchParams.city}, {result.searchParams.state} {result.searchParams.zip}

                  {/* Transaction ID display and status checker */}
                  {result.response?.data?.transaction_id && (
                    <div className="transaction-info">
                      <strong>Transaction ID:</strong> {result.response.data.transaction_id}
                      <button
                        className="status-check-btn"
                        onClick={() => checkTransactionStatus(result.response.data.transaction_id, index)}
                        disabled={pollingTransactions[result.response.data.transaction_id] === 'loading'}
                      >
                        {pollingTransactions[result.response.data.transaction_id] === 'loading'
                          ? '⏳ Checking...'
                          : pollingTransactions[result.response.data.transaction_id]
                          ? `🔄 Refresh Status (${pollingTransactions[result.response.data.transaction_id]})`
                          : '🔍 Check Status'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Status Check Results */}
                {result.statusCheck && (
                  <div className="status-check-result">
                    <div className="status-check-header">
                      <strong>Status Check Result:</strong>
                      <span className={`status-badge ${result.statusCheck.status}`}>
                        {result.statusCheck.status || 'Unknown'}
                      </span>
                      <span className="timestamp">
                        {new Date(result.statusCheck.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    {result.statusCheck.transactionDetails && (
                      <div className="transaction-details">
                        <div><strong>Product:</strong> {result.statusCheck.transactionDetails.product}</div>
                        <div><strong>As Of Date:</strong> {result.statusCheck.transactionDetails.asOfDate}</div>
                        <div><strong>Confirmation #:</strong> {result.statusCheck.transactionDetails.confirmationNumber}</div>
                        <div><strong>Client Reference:</strong> {result.statusCheck.transactionDetails.clientReference}</div>
                      </div>
                    )}
                    <details className="status-details">
                      <summary>View Full Status Response</summary>
                      <pre className="data-content">
                        {result.statusCheck.rawResponse || JSON.stringify(result.statusCheck.fullResponse, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}

                {showRawRequest && (
                  <div className="result-data">
                    <div className="data-header">
                      <strong>Request Body:</strong>
                      <button
                        className="copy-btn"
                        onClick={() => copyToClipboard(JSON.stringify(result.requestBody, null, 2))}
                      >
                        📋 Copy
                      </button>
                    </div>
                    <details className="status-details" open>
                      <summary>View Request JSON</summary>
                      <pre className="data-content">
                        {JSON.stringify(result.requestBody, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}

                {showRawResponse && (
                  <div className="result-data">
                    <div className="data-header">
                      <strong>Response:</strong>
                      <button
                        className="copy-btn"
                        onClick={() => copyToClipboard(formatJSON(result.rawResponse, result.response))}
                      >
                        📋 Copy
                      </button>
                    </div>
                    <details className="status-details" open>
                      <summary>View Response JSON</summary>
                      <pre className="data-content">
                        {formatJSON(result.rawResponse, result.response)}
                      </pre>
                    </details>
                  </div>
                )}

                {result.error && (
                  <div className="error-message">
                    <strong>Error:</strong> {result.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </div>
      )}

      {/* Save Success Modal */}
      {saveSuccessModal && (
        <div className="save-success-modal-overlay" onClick={() => setSaveSuccessModal(null)}>
          <div className="save-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="save-success-modal-header">
              <h3>✓ Report & Documents Saved</h3>
              <button className="modal-close-btn" onClick={() => setSaveSuccessModal(null)}>×</button>
            </div>
            <div className="save-success-modal-content">
              <div className="save-success-directory">
                <strong>Server Directory:</strong>
                {saveSuccessModal.directory}
              </div>

              <div className="save-success-files">
                <h4>Saved Files ({saveSuccessModal.files.length}):</h4>
                <textarea
                  className="save-success-textarea"
                  readOnly
                  value={saveSuccessModal.files.join('\n')}
                  onClick={(e) => e.target.select()}
                />
              </div>

              <div className="save-success-actions">
                <button
                  className="save-success-copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(saveSuccessModal.files.join('\n'));
                  }}
                >
                  📋 Copy File List
                </button>
                <button
                  className="save-success-close-btn"
                  onClick={() => setSaveSuccessModal(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTraceAPITester;
